import React, { useState, useEffect } from 'react';
import authService from '../services/authService';
import apiClient from '../services/apiClient';

/**
 * Authentication Test Component
 * Comprehensive testing of authentication flow with detailed logging
 */
const AuthTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [authStatus, setAuthStatus] = useState({});

  useEffect(() => {
    updateAuthStatus();
  }, []);

  const updateAuthStatus = () => {
    const token = authService.getToken();
    const status = {
      isAuthenticated: authService.isAuthenticated(),
      hasToken: !!token,
      hasStoredUser: !!authService.getStoredUser(),
      token: token ? `${token.substring(0, 20)}...` : 'None',
      storedUser: authService.getStoredUser()
    };
    setAuthStatus(status);
  };

  const tests = [
    {
      name: 'Backend Health Check',
      test: async () => {
        const response = await fetch('http://localhost:8080/health');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return { status: response.status, data };
      }
    },
    {
      name: 'API Client Configuration Check',
      test: async () => {
        return {
          baseURL: apiClient.defaults.baseURL,
          timeout: apiClient.defaults.timeout,
          headers: apiClient.defaults.headers,
          configured: !!apiClient.defaults.baseURL
        };
      }
    },
    {
      name: 'Pre-Auth Status Check',
      test: async () => {
        return {
          isAuthenticated: authService.isAuthenticated(),
          hasToken: !!authService.getToken(),
          hasStoredUser: !!authService.getStoredUser()
        };
      }
    },
    {
      name: 'Login Test (Admin User)',
      test: async () => {
        console.log('[AuthTest] Starting login test');
        const result = await authService.login('admin@hraisystem.com', 'admin123');
        console.log('[AuthTest] Login test completed', result);
        
        updateAuthStatus(); // Update status after login
        
        return {
          success: true,
          hasToken: !!result.token,
          hasUser: !!result.user,
          userEmail: result.user?.email,
          userRole: result.user?.role,
          tokenLength: result.token?.length
        };
      }
    },
    {
      name: 'Post-Login Status Check',
      test: async () => {
        return {
          isAuthenticated: authService.isAuthenticated(),
          hasToken: !!authService.getToken(),
          hasStoredUser: !!authService.getStoredUser(),
          storedUserEmail: authService.getStoredUser()?.email
        };
      }
    },
    {
      name: 'Protected Route Test (Get Current User)',
      test: async () => {
        console.log('[AuthTest] Testing protected route');
        const result = await authService.getCurrentUser();
        console.log('[AuthTest] Protected route test completed', result);
        
        return {
          success: true,
          userId: result?.id,
          userEmail: result?.email,
          userRole: result?.role,
          authenticated: true
        };
      }
    },
    {
      name: 'API Request with Token Test',
      test: async () => {
        console.log('[AuthTest] Testing API request with token');
        const response = await apiClient.get('/candidates');
        console.log('[AuthTest] API request test completed', response.status);
        
        return {
          success: true,
          status: response.status,
          hasData: !!response.data,
          dataType: typeof response.data,
          candidateCount: response.data?.data?.length || 0
        };
      }
    },
    {
      name: 'Logout Test',
      test: async () => {
        console.log('[AuthTest] Starting logout test');
        // Store current auth status
        const preLogout = {
          wasAuthenticated: authService.isAuthenticated(),
          hadToken: !!authService.getToken()
        };
        
        // Clear auth data manually (without redirect)
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        sessionStorage.removeItem('authToken');
        
        updateAuthStatus(); // Update status after logout
        
        const postLogout = {
          isAuthenticated: authService.isAuthenticated(),
          hasToken: !!authService.getToken()
        };
        
        console.log('[AuthTest] Logout test completed');
        
        return {
          success: true,
          preLogout,
          postLogout,
          logoutSuccessful: !postLogout.isAuthenticated
        };
      }
    }
  ];

  const runTest = async (testConfig) => {
    const startTime = Date.now();
    console.log(`[AuthTest] Running: ${testConfig.name}`);
    
    try {
      const result = await testConfig.test();
      const duration = Date.now() - startTime;
      
      console.log(`[AuthTest] ✅ ${testConfig.name} PASSED`, result);
      
      return {
        name: testConfig.name,
        success: true,
        result,
        duration,
        error: null
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      
      console.error(`[AuthTest] ❌ ${testConfig.name} FAILED`, {
        error: error.message,
        stack: error.stack,
        response: error.response?.data
      });
      
      return {
        name: testConfig.name,
        success: false,
        result: null,
        duration,
        error: error.message
      };
    }
  };

  const runAllTests = async () => {
    console.log('[AuthTest] Starting comprehensive authentication tests');
    setIsRunning(true);
    setTestResults([]);
    
    const results = [];
    
    for (const test of tests) {
      const result = await runTest(test);
      results.push(result);
      setTestResults([...results]); // Update UI after each test
      
      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    console.log('[AuthTest] All tests completed', {
      totalTests: results.length,
      passed: results.filter(r => r.success).length,
      failed: results.filter(r => !r.success).length
    });
    
    setIsRunning(false);
  };

  const clearAuthData = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('currentUser');
    sessionStorage.removeItem('authToken');
    updateAuthStatus();
    console.log('[AuthTest] Auth data cleared manually');
  };

  const getStatusColor = (success) => {
    return success ? 'text-green-600' : 'text-red-600';
  };

  const getStatusIcon = (success) => {
    return success ? '✅' : '❌';
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Authentication System Test Suite
      </h2>
      
      {/* Current Auth Status */}
      <div className="mb-6 p-4 bg-gray-100 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Current Authentication Status</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium">Authenticated:</span> 
            <span className={authStatus.isAuthenticated ? 'text-green-600' : 'text-red-600'}>
              {authStatus.isAuthenticated ? 'Yes' : 'No'}
            </span>
          </div>
          <div>
            <span className="font-medium">Has Token:</span> 
            <span className={authStatus.hasToken ? 'text-green-600' : 'text-red-600'}>
              {authStatus.hasToken ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="col-span-2">
            <span className="font-medium">Token Preview:</span> 
            <code className="ml-2 text-xs bg-gray-200 p-1 rounded">{authStatus.token}</code>
          </div>
          <div className="col-span-2">
            <span className="font-medium">Stored User:</span> 
            <code className="ml-2 text-xs bg-gray-200 p-1 rounded">
              {authStatus.storedUser ? JSON.stringify(authStatus.storedUser, null, 2) : 'None'}
            </code>
          </div>
        </div>
      </div>

      {/* Control Buttons */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={runAllTests}
          disabled={isRunning}
          className={`px-6 py-3 rounded-lg font-medium ${
            isRunning 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {isRunning ? 'Running Tests...' : 'Run Authentication Tests'}
        </button>
        
        <button
          onClick={clearAuthData}
          className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
        >
          Clear Auth Data
        </button>
        
        <button
          onClick={updateAuthStatus}
          className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
        >
          Refresh Status
        </button>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700">Test Results:</h3>
          
          {testResults.map((result, index) => (
            <div 
              key={index}
              className={`p-4 border rounded-lg ${
                result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className={`font-medium ${getStatusColor(result.success)}`}>
                  {getStatusIcon(result.success)} {result.name}
                </h4>
                <span className="text-sm text-gray-500">
                  {result.duration}ms
                </span>
              </div>
              
              {result.success ? (
                <div className="text-sm text-green-700">
                  <strong>Success!</strong>
                  {result.result && (
                    <pre className="mt-2 p-2 bg-green-100 rounded text-xs overflow-auto">
                      {JSON.stringify(result.result, null, 2)}
                    </pre>
                  )}
                </div>
              ) : (
                <div className="text-sm text-red-700">
                  <strong>Error:</strong> {result.error}
                </div>
              )}
            </div>
          ))}
          
          {!isRunning && testResults.length === tests.length && (
            <div className="mt-6 p-4 bg-gray-100 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-2">Test Summary:</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-green-600">✅ Passed: </span>
                  {testResults.filter(r => r.success).length}
                </div>
                <div>
                  <span className="text-red-600">❌ Failed: </span>
                  {testResults.filter(r => !r.success).length}
                </div>
              </div>
              
              {testResults.some(r => !r.success) && (
                <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded">
                  <h5 className="font-medium text-yellow-800 mb-2">Troubleshooting:</h5>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• Check browser console for detailed error logs</li>
                    <li>• Ensure backend is running: cd backend && cargo run</li>
                    <li>• Verify database connection and migrations</li>
                    <li>• Check network connectivity to localhost:8080</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuthTest;